// apps/client/src/components/layout/Header.tsx
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ChevronRight, Home, User, Settings, LogOut } from "lucide-react";
import { useUIStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";

export default function Header() {
  // ✅ removed sidebarCollapsed — offset is now handled by ClientLayout parent div
  const { toggleSidebar, setMobileOpen } = useUIStore();
  const { user, logout } = useAuthStore();
  const breadcrumbs = useBreadcrumbs();
  const navigate = useNavigate();

  const [userOpen, setUserOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userRef.current && !userRef.current.contains(e.target as Node))
        setUserOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  // ✅ No offset variable — ClientLayout's parent div already applies lg:pl-[240px] / lg:pl-[68px]

  return (
    // ✅ Changed: fixed → sticky top-0 | removed left-0 right-0 | removed offset class
    <header className="sticky top-0 z-20 flex h-16 items-center border-b border-slate-200 bg-white px-4 shadow-sm">
      {/* Left: hamburger + breadcrumbs */}
      <div className="flex flex-1 items-center gap-3 overflow-hidden">
        <button
          onClick={toggleSidebar}
          className="hidden rounded-md p-1.5 text-slate-500 hover:bg-slate-100 transition lg:block"
        >
          <Menu className="h-5 w-5" />
        </button>
        <button
          onClick={() => setMobileOpen(true)}
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 transition lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Breadcrumbs */}
        <nav className="hidden items-center gap-1 text-sm sm:flex">
          <Link
            to="/dashboard"
            className="text-slate-400 hover:text-[#1B4FD8] transition"
          >
            <Home className="h-4 w-4" />
          </Link>
          {breadcrumbs.map((crumb, i) => (
            <span key={crumb.href} className="flex items-center gap-1">
              <ChevronRight className="h-4 w-4 text-slate-300" />
              {i === breadcrumbs.length - 1 ? (
                <span className="font-medium text-slate-700">
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.href}
                  className="text-slate-400 hover:text-[#1B4FD8] transition"
                >
                  {crumb.label}
                </Link>
              )}
            </span>
          ))}
        </nav>
      </div>

      {/* Right: notifications + user */}
      <div className="flex items-center gap-2">
        {/* User menu */}
        <div ref={userRef} className="relative">
          <button
            onClick={() => {
              setUserOpen((p) => !p);
            }}
            className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-100 transition"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1B4FD8] text-sm font-semibold text-white">
              {(user?.firstName ?? user?.name ?? "U").charAt(0).toUpperCase()}
            </div>
            <div className="hidden text-left sm:block">
              <p className="text-sm font-medium text-slate-700 leading-tight">
                {user?.firstName
                  ? user.firstName + (user.lastName ? " " + user.lastName : "")
                  : (user?.name ?? "User")}
              </p>
              <p className="text-[11px] capitalize text-slate-400">
                {user?.role === "client" && user?.clientRole
                  ? user.clientRole.replace("_", " ")
                  : (user?.role?.replace("_", " ") ?? "")}
              </p>
            </div>
          </button>

          {userOpen && (
            <div className="absolute right-0 top-full mt-2 w-52 rounded-xl border border-slate-200 bg-white shadow-lg">
              <div className="border-b border-slate-100 px-4 py-3">
                <p className="text-sm font-semibold text-slate-800">
                  {user?.name}
                </p>
                <p className="truncate text-xs text-slate-400">{user?.email}</p>
              </div>
              <ul className="p-1.5">
                {[
                  { label: "My Profile", icon: User, href: "/profile" },
                  { label: "Settings", icon: Settings, href: "/settings" },
                ].map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      onClick={() => setUserOpen(false)}
                      className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition"
                    >
                      <item.icon className="h-4 w-4 text-slate-400" />
                      {item.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <div className="my-1 h-px bg-slate-100" />
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition"
                  >
                    <LogOut className="h-4 w-4" />
                    Log out
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// Day 14 complete
